import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { BankTemplateSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ledgerBankTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/bank-templates',
  component: LedgerBankTemplatesPage,
});

type FormValues = {
  name: string;
  fileFormat: 'csv' | 'xlsx' | 'pdf';
  configJson: string;
};

type BankTemplateSortKey = 'name' | 'format' | 'status';
const EMPTY_TEMPLATES: BankTemplateSummary[] = [];
const templateSortAccessors: SortAccessors<BankTemplateSummary, BankTemplateSortKey> = {
  name: (t) => t.name,
  format: (t) => t.fileFormat,
  status: (t) => t.active,
};

const EMPTY_CONFIG_HINT = JSON.stringify(
  {
    headerRowIndex: 0,
    columns: {
      date: { at: 'Date' },
      amount: { at: 'Amount' },
      direction: { at: 'Type', map: { Cr: 'in', Dr: 'out' } },
      description: { at: 'Narration' },
    },
    amountPolicy: 'signed',
    dateFormat: 'DD/MM/YYYY',
    openingBalanceRow: { strategy: 'none' },
    closingBalanceRow: { strategy: 'none' },
  },
  null,
  2,
);

const PDF_CONFIG_HINT = JSON.stringify(
  {
    headerRowIndex: 0,
    columns: {},
    amountPolicy: 'withdrawal_deposit_columns',
    dateFormat: 'DD/MM/YYYY',
    pdf: {
      skipLinePatterns: [
        '^Statement of Account',
        '^Account Number',
        '^Page \\d+ of \\d+',
      ],
      rowPattern:
        '^(?<date>\\d{2}/\\d{2}/\\d{4})\\s+(?<description>.+?)\\s+(?<withdrawal>[\\d,]*\\.?\\d*)\\s+(?<deposit>[\\d,]*\\.?\\d*)\\s+[\\d,]+\\.\\d{2}\\s*$',
      openingBalancePattern: 'Opening Balance\\s*[:\\-]?\\s*([\\d,]+\\.\\d{2})',
      closingBalancePattern: 'Closing Balance\\s*[:\\-]?\\s*([\\d,]+\\.\\d{2})',
      continuationJoin: false,
    },
  },
  null,
  2,
);

function LedgerBankTemplatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['bank-templates'],
    queryFn: () => api.listBankTemplates(),
  });

  const { rows: templates, ...sorter } = useSortedRows(listQuery.data ?? EMPTY_TEMPLATES, templateSortAccessors);

  const form = useForm<FormValues>({
    defaultValues: { name: '', fileFormat: 'csv', configJson: EMPTY_CONFIG_HINT },
  });

  const createMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(values.configJson);
      } catch (e) {
        throw new Error(`Config is not valid JSON: ${(e as Error).message}`);
      }
      return api.createBankTemplate({
        name: values.name,
        fileFormat: values.fileFormat,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        config: parsed as any,
      });
    },
    onSuccess: async () => {
      setDialogOpen(false);
      form.reset({ name: '', fileFormat: 'csv', configJson: EMPTY_CONFIG_HINT });
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['bank-templates'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateBankTemplate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bank-templates'] });
    },
  });

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 lg:flex-nowrap">
        <div className="min-w-0">
          <h1 className="font-display text-3xl">Bank templates</h1>
          <p className="text-muted-foreground mt-1">
            Column mappings per bank. Add one per bank statement export format you use.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>New template</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : listQuery.data && listQuery.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[28rem] text-sm [&_td]:pr-3 [&_th]:pr-3">
                <thead>
                  <tr className="border-b text-left">
                    <SortableTh sortKey="name" controller={sorter} className="py-2">
                      Name
                    </SortableTh>
                    <SortableTh sortKey="format" controller={sorter} className="py-2">
                      Format
                    </SortableTh>
                    <SortableTh sortKey="status" controller={sorter} className="py-2">
                      Status
                    </SortableTh>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-b">
                      <td className="py-2">{t.name}</td>
                      <td className="py-2 uppercase">{t.fileFormat}</td>
                      <td className="py-2">
                        {t.active ? <Badge>Active</Badge> : <Badge variant="destructive">Inactive</Badge>}
                      </td>
                      <td className="py-2 text-right">
                        {t.active && (
                          <Button size="sm" variant="ghost" onClick={() => deactivateMutation.mutate(t.id)}>
                            Deactivate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground">No templates yet — 4 defaults are added by the seed.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New bank template</DialogTitle>
            <DialogDescription>Configure how a bank statement export maps to columns.</DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register('name', { required: true })} />
            </div>
            <div>
              <Label htmlFor="fileFormat">File format</Label>
              <select
                id="fileFormat"
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                {...form.register('fileFormat', {
                  onChange: (e) => {
                    const next = (e.target as HTMLSelectElement).value;
                    const hint = next === 'pdf' ? PDF_CONFIG_HINT : EMPTY_CONFIG_HINT;
                    form.setValue('configJson', hint);
                  },
                })}
              >
                <option value="csv">CSV</option>
                <option value="xlsx">XLSX</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <div>
              <Label htmlFor="configJson">Config (JSON)</Label>
              <Textarea id="configJson" rows={16} className="font-mono text-xs" {...form.register('configJson')} />
            </div>
            {errorMessage && (
              <Alert variant="destructive">
                <AlertTitle>Failed</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
